import { Suspense } from 'react'
import { safeLazy } from '@/lib/chunk-error-handler'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useRouteError,
} from 'react-router-dom'
import { useIsPlatformAdmin } from '@/lib/permissions'
import { usePlatformTenantOptional } from '@/features/platform/context'
import { OnboardingLayout, OnboardingHome, OnboardingDoc, OnboardingExample } from '@/features/onboarding'
import { AppShell } from '@/components/layout/AppShell'
import { ForgotPasswordPage, LoginPage } from '@/features/auth/routes'
import { useMe } from '@/features/auth/hooks'
import { pickLandingRoute } from '@/features/auth/landing'
import { Spinner } from '@/components/ui/Spinner'
import { useCanAny } from '@/lib/permissions'
import { Button } from '@/components/ui/Button'
import { AccessDenied } from '@/components/AccessDenied'

import {
  AlertsQueuePage,
  AlertDetailPage,
} from '@/features/alerts/routes'
import { CasesListPage, CaseDetailPage } from '@/features/cases/routes'
import { AccountDetailPage } from '@/features/customers/routes'

// AF-131 — Customer 360 is lazy-loaded so Recharts + react-force-graph
// stay off the main bundle for analysts who never open it.
const Customer360Page = safeLazy(() =>
  import('@/features/customers/Customer360').then((m) => ({
    default: m.Customer360Page,
  })),
)
// Customer search hub — the /customers landing page that the Sidebar
// links to. Lazy so its small footprint doesn't bloat the alerts hot-path.
const CustomersListPage = safeLazy(() =>
  import('@/features/customers/CustomersList').then((m) => ({
    default: m.CustomersListPage,
  })),
)
import {
  BlockRequestPage,
  BlockApprovalQueuePage,
  ActiveBlocksPage,
  BlockDetailPage,
} from '@/features/blocks/routes'
// AF-136/137/133 — SAR + Reports + ML registry are admin/compliance
// surfaces seen by a smaller subset of users. Lazy-loading keeps their
// chunks off the main bundle so the analyst's daily-use pages stay fast.
const SarListPage = safeLazy(() =>
  import('@/features/sars/routes').then((m) => ({ default: m.SarListPage })),
)
const SarWizardPage = safeLazy(() =>
  import('@/features/sars/routes').then((m) => ({ default: m.SarWizardPage })),
)
const ReportsCatalogPage = safeLazy(() =>
  import('@/features/reports/routes').then((m) => ({
    default: m.ReportsCatalogPage,
  })),
)
const ReportDetailPage = safeLazy(() =>
  import('@/features/reports/routes').then((m) => ({
    default: m.ReportDetailPage,
  })),
)
const ReportsSchedulePage = safeLazy(() =>
  import('@/features/reports/routes').then((m) => ({
    default: m.ReportsSchedulePage,
  })),
)
const MlModelsPage = safeLazy(() =>
  import('@/features/ml/routes').then((m) => ({ default: m.MlModelsPage })),
)
const MlModelDetailPage = safeLazy(() =>
  import('@/features/ml/routes').then((m) => ({
    default: m.MlModelDetailPage,
  })),
)
const MlModelComparePage = safeLazy(() =>
  import('@/features/ml/routes').then((m) => ({
    default: m.MlModelComparePage,
  })),
)
const MlMonitoringPage = safeLazy(() =>
  import('@/features/ml/MlMonitoringPage').then((m) => ({
    default: m.MlMonitoringPage,
  })),
)
const MlGovernancePage = safeLazy(() =>
  import('@/features/ml/MlGovernancePage').then((m) => ({
    default: m.MlGovernancePage,
  })),
)
// Screening dashboard. Lazy so the screening bundle (matcher
// visualisations, dashboard charts) stays off the analyst's daily
// hot-path. The old /screening/sources catalogue page was folded
// into /screening/console (engine status + catalogue + activity).
const ScreeningDashboardPage = safeLazy(() =>
  import('@/features/screening/routes').then((m) => ({
    default: m.ScreeningDashboardPage,
  })),
)
const ScreeningConsolePage = safeLazy(() =>
  import('@/features/screening/routes').then((m) => ({
    default: m.ScreeningConsolePage,
  })),
)
const ScreeningDetailPage = safeLazy(() =>
  import('@/features/screening/routes').then((m) => ({
    default: m.ScreeningDetailPage,
  })),
)
const ScreeningHistoryPage = safeLazy(() =>
  import('@/features/screening/routes').then((m) => ({
    default: m.ScreeningHistoryPage,
  })),
)
// Adverse-media feature surface. Lazy so the bundle (LLM-call
// trail rendering, dashboard charts, feed admin) stays off the
// analyst's daily hot-path.
const AdverseMediaDashboardPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaDashboardPage,
  })),
)
const AdverseMediaFeedsPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaFeedsPage,
  })),
)
const AdverseMediaClassificationsPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaClassificationsPage,
  })),
)
const AdverseMediaClassificationDetailPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaClassificationDetailPage,
  })),
)
const AdverseMediaRelatedPartyDetailPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaRelatedPartyDetailPage,
  })),
)
const AdverseMediaMerchantDetailPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaMerchantDetailPage,
  })),
)
const AdverseMediaProfilesPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaProfilesPage,
  })),
)
const AdverseMediaEvalSetPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaEvalSetPage,
  })),
)
const AdverseMediaPromptsPage = safeLazy(() =>
  import('@/features/adverse_media/routes').then((m) => ({
    default: m.AdverseMediaPromptsPage,
  })),
)
const JobsPage = safeLazy(() =>
  import('@/features/admin/jobs/JobsPage').then((m) => ({ default: m.JobsPage })),
)
import {
  EndShiftPage,
  HandoverAcknowledgePage,
  HandoverLogPage,
} from '@/features/handovers/routes'
import {
  NotificationsCenterPage,
  NotificationPreferencesPage,
} from '@/features/notifications/routes'
const AccessRequestsInboxPage = safeLazy(() =>
  import('@/features/access-requests/InboxPage').then((m) => ({
    default: m.AccessRequestsInboxPage,
  })),
)
const MyAccessRequestsPage = safeLazy(() =>
  import('@/features/access-requests/MyRequestsPage').then((m) => ({
    default: m.MyAccessRequestsPage,
  })),
)
// Admin scenarios — lazy-loaded so the admin surface doesn't pull
// every page into the main bundle (`features/admin/routes` re-exports
// ~30 admin pages eagerly otherwise).
const AdminScenariosPage = safeLazy(() =>
  import('@/features/admin/routes').then((m) => ({
    default: m.AdminScenariosPage,
  })),
)
const AdminScenarioDetailPage = safeLazy(() =>
  import('@/features/admin/routes').then((m) => ({
    default: m.AdminScenarioDetailPage,
  })),
)
// ADR-0023 tenant-scoping: layout wraps /admin/scenarios* with
// PlatformTenantProvider + TenantSelectorBand so the platform admin can
// pick a tenant before viewing or editing scenarios.
import { AdminScenariosLayout } from '@/features/admin/scenarios/AdminScenariosLayout'
import { AdminRulesLayout } from '@/features/admin/rules/AdminRulesLayout'
// Slides #7, #21, #23, #27 — design-deck pages added on top of the
// existing analyst surface. Each is lazy so they don't bloat the
// alerts/cases hot-path bundle.
const ExecutiveDashboardPage = safeLazy(() =>
  import('@/features/dashboard/ExecutiveDashboard').then((m) => ({
    default: m.ExecutiveDashboardPage,
  })),
)
const EnterpriseHighlightsPage = safeLazy(() =>
  import('@/features/dashboard/EnterpriseHighlightsPage').then((m) => ({
    default: m.EnterpriseHighlightsPage,
  })),
)
const DashboardPortfolioPage = safeLazy(() =>
  import('@/features/dashboard/PortfolioPage').then((m) => ({
    default: m.DashboardPortfolioPage,
  })),
)
const MyDashboardPage = safeLazy(() =>
  import('@/features/dashboard/MyDashboard').then((m) => ({
    default: m.MyDashboard,
  })),
)
const HQRollupDashboardPage = safeLazy(() =>
  import('@/features/dashboard/HQRollupDashboard').then((m) => ({
    default: m.HQRollupDashboard,
  })),
)
const ComplianceDashboardPage = safeLazy(() =>
  import('@/features/dashboard/ComplianceDashboard').then((m) => ({
    default: m.ComplianceDashboardPage,
  })),
)
const RoleBasedDashboardsPage = safeLazy(() =>
  import('@/features/dashboard/RoleBasedDashboardsPage').then((m) => ({
    default: m.RoleBasedDashboardsPage,
  })),
)
const AnalyticsDashboardPage = safeLazy(() =>
  import('@/features/analytics/AnalyticsDashboard').then((m) => ({
    default: m.AnalyticsDashboardPage,
  })),
)
const AuditLogPage = safeLazy(() =>
  import('@/features/audit/AuditLog').then((m) => ({ default: m.AuditLogPage })),
)
const RoleDefinitionsPage = safeLazy(() =>
  import('@/features/admin/roles/RoleDefinitions').then((m) => ({
    default: m.RoleDefinitionsPage,
  })),
)
const SettingsHubPage = safeLazy(() =>
  import('@/features/settings/SettingsHub').then((m) => ({ default: m.SettingsHubPage })),
)
const ChallengesPage = safeLazy(() =>
  import('@/features/admin/challenges/ChallengesPage').then((m) => ({
    default: m.ChallengesPage,
  })),
)
const ChallengePlaneSettingsPage = safeLazy(() =>
  import('@/features/admin/challenges/PlaneSettingsPage').then((m) => ({
    default: m.PlaneSettingsPage,
  })),
)
const MLFeedbackPage = safeLazy(() =>
  import('@/features/admin/ml-feedback/MLFeedbackPage').then((m) => ({
    default: m.MLFeedbackPage,
  })),
)
const ApiKeysPage = safeLazy(() =>
  import('@/features/admin/api-keys/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })),
)
const MakerCheckerPage = safeLazy(() =>
  import('@/features/admin/maker-checker/MakerCheckerPage').then((m) => ({
    default: m.MakerCheckerPage,
  })),
)
const WebhooksPage = safeLazy(() =>
  import('@/features/admin/webhooks/WebhooksPage').then((m) => ({
    default: m.WebhooksPage,
  })),
)
const CommunicationWorkflowsPage = safeLazy(() =>
  import('@/features/notifications/CommunicationWorkflowsPage').then((m) => ({
    default: m.CommunicationWorkflowsPage,
  })),
)
const CommunicationDeliveriesPage = safeLazy(() =>
  import('@/features/notifications/CommunicationDeliveriesPage').then((m) => ({
    default: m.CommunicationDeliveriesPage,
  })),
)
const FraudToAmlPage = safeLazy(() =>
  import('@/features/admin/aml/FraudToAmlPage').then((m) => ({
    default: m.FraudToAmlPage,
  })),
)
const UsersAdminPage = safeLazy(() =>
  import('@/features/admin/users/UsersAdminPage').then((m) => ({
    default: m.UsersAdminPage,
  })),
)
const IngestionEnginePage = safeLazy(() =>
  import('@/features/ingestion/IngestionEnginePage').then((m) => ({
    default: m.IngestionEnginePage,
  })),
)
const MappingEditorPage = safeLazy(() =>
  import('@/features/databus/MappingEditorPage').then((m) => ({
    default: m.MappingEditorPage,
  })),
)
const ReconRunsPage = safeLazy(() =>
  import('@/features/databus/ReconRunsPage').then((m) => ({
    default: m.ReconRunsPage,
  })),
)
const AdaptersPage = safeLazy(() =>
  import('@/features/databus/AdaptersPage').then((m) => ({
    default: m.AdaptersPage,
  })),
)
const DLQConsolePage = safeLazy(() =>
  import('@/features/databus/DLQConsolePage').then((m) => ({
    default: m.DLQConsolePage,
  })),
)
const LineageExplorerPage = safeLazy(() =>
  import('@/features/databus/LineageExplorerPage').then((m) => ({
    default: m.LineageExplorerPage,
  })),
)
const OpsDashboardPage = safeLazy(() =>
  import('@/features/databus/OpsDashboardPage').then((m) => ({
    default: m.OpsDashboardPage,
  })),
)
const PlatformSettingsPage = safeLazy(() =>
  import('@/features/databus/PlatformSettingsPage').then((m) => ({
    default: m.PlatformSettingsPage,
  })),
)
// Third-party integrations admin (AF-089 + AF-DB-FAB-400). Standing
// rule: every external system the platform integrates with is
// managed here. Backed by case-service /admin/integrations against
// config.integrations (42 vendor rows: Dojah, OFAC, NIBSS, anthropic,
// gdelt, comms, …) + the 8 streaming-fabric rows we just added
// (MaxMind, DB-IP, IP2Location, IPLocate, Apicurio, MinIO,
// feature_redis, KuzuDB).
const IntegrationsPage = safeLazy(() =>
  import('@/features/admin/integrations/IntegrationsPage').then((m) => ({
    default: m.IntegrationsPage,
  })),
)
const SchemaRegistryPage = safeLazy(() =>
  import('@/features/databus/SchemaRegistryPage').then((m) => ({
    default: m.SchemaRegistryPage,
  })),
)
const CreateAdapterPage = safeLazy(() =>
  import('@/features/databus/CreateAdapterPage').then((m) => ({
    default: m.CreateAdapterPage,
  })),
)
const AdapterDetailPage = safeLazy(() =>
  import('@/features/databus/AdapterDetailPage').then((m) => ({
    default: m.AdapterDetailPage,
  })),
)
const CreateTenantPage = safeLazy(() =>
  import('@/features/databus/CreateTenantPage').then((m) => ({
    default: m.CreateTenantPage,
  })),
)
// ADR 0023 Sprint 5b — Entry Point B: "Add Connector to Tenant".
// Replaces the old OnboardingWizard combined flow. Calls the single
// ingestion-channel provisioning saga (POST /databus/ingestion-channels).
const ConfigureIngestionChannelPage = safeLazy(() =>
  import('@/features/databus/ConfigureIngestionChannelPage').then((m) => ({
    default: m.ConfigureIngestionChannelPage,
  })),
)
// ADR 0023 Sprint 6c — Approval queue + Activation checklist.
const ApprovalsQueuePage = safeLazy(() =>
  import('@/features/databus/ApprovalsQueuePage').then((m) => ({
    default: m.ApprovalsQueuePage,
  })),
)
const ActivationChecklistPage = safeLazy(() =>
  import('@/features/databus/ActivationChecklistPage').then((m) => ({
    default: m.ActivationChecklistPage,
  })),
)
// AF-SIM-062/063/064/065 — Banking simulator surfaces. Reused chrome
// from the databus operator pages.
const SimulatorRunsListPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.RunsListPage })),
)
const SimulatorRunDetailPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.RunDetailPage })),
)
const SimulatorScenariosListPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.ScenariosListPage })),
)
const SimulatorScenarioEditorPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.ScenarioEditorPage })),
)
const SimulatorProfileEditorPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.ProfileEditorPage })),
)
const SimulatorChaosRehearsalPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.ChaosRehearsalPage })),
)
const SimulatorAllowlistPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.AllowlistPage })),
)
const SimulatorSystemPage = safeLazy(() =>
  import('@/features/simulator').then((m) => ({ default: m.SystemPage })),
)
const TenantDetailPage = safeLazy(() =>
  import('@/features/databus/TenantDetailPage').then((m) => ({
    default: m.TenantDetailPage,
  })),
)
const FeatureFlagsPage = safeLazy(() =>
  import('@/features/databus/FeatureFlagsPage').then((m) => ({
    default: m.FeatureFlagsPage,
  })),
)
const LineageDetailPage = safeLazy(() =>
  import('@/features/databus/LineageDetailPage').then((m) => ({
    default: m.LineageDetailPage,
  })),
)
const DLQEntryDetailPage = safeLazy(() =>
  import('@/features/databus/DLQEntryDetailPage').then((m) => ({
    default: m.DLQEntryDetailPage,
  })),
)
const SystemStatusPage = safeLazy(() =>
  import('@/features/admin/system-status/SystemStatusPage').then((m) => ({
    default: m.SystemStatusPage,
  })),
)
// AF-OPS-001 — dev/staging services control plane (start/stop/restart + console).
const ServicesControlPage = safeLazy(() =>
  import('@/features/admin/services/ServicesControlPage').then((m) => ({
    default: m.ServicesControlPage,
  })),
)
const BatchConsolePage = safeLazy(() =>
  import('@/features/admin/BatchConsolePage').then((m) => ({
    default: m.BatchConsolePage,
  })),
)
// ADR 0013 L6 — outbox replay maker-checker admin surface.
const OutboxReplayPage = safeLazy(() =>
  import('@/features/outbox-replay/OutboxReplayPage').then((m) => ({
    default: m.OutboxReplayPage,
  })),
)
const NetworkExplorerPage = safeLazy(() =>
  import('@/features/investigations/NetworkExplorerPage').then((m) => ({
    default: m.NetworkExplorerPage,
  })),
)
const ConsortiumPage = safeLazy(() =>
  import('@/features/intelligence/ConsortiumPage').then((m) => ({
    default: m.ConsortiumPage,
  })),
)
const AccessLogPage = safeLazy(() =>
  import('@/features/admin/access-log/AccessLogPage').then((m) => ({
    default: m.AccessLogPage,
  })),
)
// ADR 0018 — Observability network allowlist admin page.
// Gated by observability:read at minimum; the page enforces granular
// propose/approve/emergency affordances internally.
const ObservabilityACLPage = safeLazy(() =>
  import('@/features/admin/observability/AccessControlPage').then((m) => ({
    default: m.AccessControlPage,
  })),
)
const BranchesAdminPage = safeLazy(() =>
  import('@/features/admin/branches/BranchesPage').then((m) => ({
    default: m.BranchesAdminPage,
  })),
)
const TeamsAdminPage = safeLazy(() =>
  import('@/features/admin/teams/TeamsPage').then((m) => ({
    default: m.TeamsAdminPage,
  })),
)
const DemoDataConsolePage = safeLazy(() =>
  import('@/features/admin/demo-data/Console').then((m) => ({
    default: m.DemoDataConsolePage,
  })),
)
const InboxPage = safeLazy(() =>
  import('@/features/inbox/InboxPage').then((m) => ({
    default: m.InboxPage,
  })),
)
const OperationalDashboardPage = safeLazy(() =>
  import('@/features/operations/OperationalDashboard').then((m) => ({
    default: m.OperationalDashboardPage,
  })),
)
const RulesListPage = safeLazy(() =>
  import('@/features/admin/rules/RulesListPage').then((m) => ({
    default: m.RulesListPage,
  })),
)
const EditRulePage = safeLazy(() =>
  import('@/features/admin/rules/EditRulePage').then((m) => ({
    default: m.EditRulePage,
  })),
)
const CreateRuleWizardPage = safeLazy(() =>
  import('@/features/admin/rules/CreateRuleWizard').then((m) => ({
    default: m.CreateRuleWizardPage,
  })),
)
const CreateScenarioPage = safeLazy(() =>
  import('@/features/admin/scenarios/CreatePage').then((m) => ({
    default: m.CreateScenarioPage,
  })),
)
// AF-130 — workflow pages are lazy-loaded to keep Recharts (~40KB gz) +
// xyflow chunks off the main bundle for analysts who don't open them.
const StateMachineViewPage = safeLazy(() =>
  import('@/features/workflow/StateMachineView').then((m) => ({
    default: m.StateMachineViewPage,
  })),
)
const QueueDashboardPage = safeLazy(() =>
  import('@/features/workflow/QueueDashboard').then((m) => ({
    default: m.QueueDashboardPage,
  })),
)
import {
  TransactionsListPage,
  TransactionDetailPage,
} from '@/features/transactions/routes'

// ADR-0023 — Tenant self-service administration.
// /tenant-admin/* routes are gated on the tenant_users:admin permission.
// This surface is primarily for tenant-scope admins managing their own tenant's users.
// Platform admins also have access via the system:admin short-circuit.
const TenantSelfUsersPage = safeLazy(() =>
  import('@/features/tenant-admin').then((m) => ({
    default: m.TenantSelfUsersPage,
  })),
)

// ADR-0025 — Tenant-scoped role management.
// /tenant-admin/roles is gated on tenant_roles:read (scope:'tenant').
// DO NOT link or share with the platform /admin/roles surface.
const TenantRolesPage = safeLazy(() =>
  import('@/features/tenant-admin/roles').then((m) => ({
    default: m.TenantRolesPage,
  })),
)

// Tenant permissions catalogue.
// /tenant-admin/permissions is gated on tenant_permissions:read (scope:'tenant').
// Read-only reference of the permission vocabulary available within this tenant's
// ceiling. DO NOT link or share with the platform /admin/permissions endpoint.
const TenantPermissionsCataloguePage = safeLazy(() =>
  import('@/features/tenant-admin/permissions').then((m) => ({
    default: m.TenantPermissionsCataloguePage,
  })),
)

// ADR-0025 Follow-on / ADR-0026 Phase B / Slice 3 — Tenant-internal maker-checker approvals inbox.
// /tenant-admin/approvals is gated on ANY of:
//   tenant_roles:approve | tenant_branches:approve | tenant_teams:approve | tenant_sso:approve
// Displays submitted/approved/applied/rejected role, branch, team, and SSO config change requests.
// Approve/reject per row additionally requires the entity-type-matching perm (server-enforced).
const TenantApprovalsInboxPage = safeLazy(() =>
  import('@/features/tenant-admin/approvals').then((m) => ({
    default: m.TenantApprovalsInboxPage,
  })),
)

// ADR-0026 Phase B — Tenant self-service branch management.
// /tenant-admin/branches is gated on tenant_branches:read (scope:'tenant').
// Uses /tenants/self/branches — tenant is derived server-side from JWT.
// DO NOT link or share with the platform /admin/branches surface.
const TenantBranchesPage = safeLazy(() =>
  import('@/features/tenant-admin/branches').then((m) => ({
    default: m.TenantBranchesPage,
  })),
)

// ADR-0026 Phase B — Tenant self-service team management.
// /tenant-admin/teams is gated on tenant_teams:read (scope:'tenant').
// Uses /tenants/self/teams and /tenants/self/users — tenant is derived server-side from JWT.
// Member picker uses /tenants/self/users, NOT the platform-only /admin/users endpoint.
// DO NOT link or share with the platform /admin/teams surface.
const TenantTeamsPage = safeLazy(() =>
  import('@/features/tenant-admin/teams').then((m) => ({
    default: m.TenantTeamsPage,
  })),
)

// Tenant session management.
// /tenant-admin/sessions is gated on tenant_users:admin (scope:'tenant').
// Uses /tenants/self/sessions — tenant is derived server-side from JWT.
// DO NOT share or link to the platform /admin/sessions surface from here.
const TenantSessionsPage = safeLazy(() =>
  import('@/features/tenant-admin/sessions').then((m) => ({
    default: m.TenantSessionsPage,
  })),
)

// Tenant self-service SSO configuration.
// /tenant-admin/sso is gated on tenant_sso:read (scope:'tenant').
// Write affordances additionally require tenant_sso:admin.
// Uses /tenants/self/sso-config — tenant is derived server-side from JWT.
// Mutations are maker-checker gated by default (security-sensitive per ADR).
// DO NOT link or share with any platform SSO surface.
const TenantSSOConfigPage = safeLazy(() =>
  import('@/features/tenant-admin/sso').then((m) => ({
    default: m.TenantSSOConfigPage,
  })),
)

// Tenant self-service JIT access approval queue.
// /tenant-admin/jit-access is gated on tenant_jit:approve (scope:'tenant').
// tenant_jit:read is NeverGrantableToTenantRole — read access is implied by approve.
// Uses /tenants/self/jit-access/pending — tenant derived server-side from JWT.
// Self-approval is blocked server-side (403 cannot_self_approve).
// DO NOT link or share with any platform JIT surface (/admin/jit-access).
const TenantJITAccessPage = safeLazy(() =>
  import('@/features/tenant-admin/jit-access').then((m) => ({
    default: m.TenantJITAccessPage,
  })),
)

// Slice 5 — Tenant self-service API key management.
// /tenant-admin/api-keys is gated on tenant_api_keys:read (scope:'tenant').
// Write affordances (create, revoke) additionally require tenant_api_keys:admin.
// Revoke mutations are maker-checker gated by default (api_key.revoke approval flow).
// Uses /tenants/self/api-keys — tenant derived server-side from JWT.
// Distinct from platform /admin/api-keys (platform-only surface, different endpoints).
// DO NOT share or link to the platform API keys surface from here.
const TenantAPIKeysPage = safeLazy(() =>
  import('@/features/tenant-admin/api-keys').then((m) => ({
    default: m.TenantAPIKeysPage,
  })),
)

// Slice 6 — Tenant self-service webhook management.
// /tenant-admin/webhooks is gated on tenant_webhooks:read (scope:'tenant').
// Write affordances (create, enable, disable) additionally require tenant_webhooks:admin.
// Creation is maker-checker gated (webhook.create approval flow;
//   entity_type: tenant_webhook_creation).
// Enable and disable (DELETE) are single-operator.
// Uses /tenants/self/webhooks — tenant derived server-side from JWT.
// DO NOT link or share with the platform /admin/webhooks surface.
const TenantWebhooksPage = safeLazy(() =>
  import('@/features/tenant-admin/webhooks').then((m) => ({
    default: m.TenantWebhooksPage,
  })),
)

// Slice 7 — Tenant self-service access-request approver inbox.
// Gated on tenant_access:approve (scope:'tenant').
// Uses /tenants/self/access-requests — tenant derived server-side from JWT.
// DISTINCT from JIT Access (/tenant-admin/jit-access — role elevation).
// DISTINCT from platform /access-requests (uses access:grant permission).
// DO NOT link or share with those surfaces.
const TenantAccessRequestsPage = safeLazy(() =>
  import('@/features/tenant-admin/access-requests').then((m) => ({
    default: m.TenantAccessRequestsPage,
  })),
)

// Slice 8 — Tenant self-service banking settings.
// Gated on tenant_bank_settings:read (scope:'tenant'); RequirePermissionRoute wraps the route.
// Uses /tenants/self/bank-settings — tenant derived server-side from JWT.
// All proposals are maker-checker gated (no direct-apply path). 202 is always returned.
const TenantBankSettingsPage = safeLazy(() =>
  import('@/features/tenant-admin/bank-settings').then((m) => ({
    default: m.TenantBankSettingsPage,
  })),
)

// Slice 9 — Tenant self-service challenge plane settings.
// Gated on tenant_challenge_settings:read (scope:'tenant'); RequirePermissionRoute wraps the route.
// Uses /tenants/self/challenge-settings — tenant derived server-side from JWT.
// All proposals are maker-checker gated (entity_type: tenant_challenge_settings_update).
// LIVE fields: liveness_min_score, liveness_face_match_min_score.
// DEFERRED fields: vendor pins, deadline overrides, max-attempt overrides (inline-liveness channel not yet shipped).
// DO NOT link or share with the platform /admin/challenges/settings surface (system:admin only).
const TenantChallengeSettingsPage = safeLazy(() =>
  import('@/features/tenant-admin/challenge-settings').then((m) => ({
    default: m.TenantChallengeSettingsPage,
  })),
)

// ADR-0023 Track 4 — Tenant self-service scenario + rule management.
// /tenant-admin/scenarios/* and /tenant-admin/rules/* routes are gated on
// scenarios:read (tenant-scope). Platform admins are rejected server-side from
// /tenants/self/* endpoints (403); the scope:'tenant' nav gate prevents them
// from seeing the links. Backend returns 404 for cross-tenant access attempts.
const TenantScenariosListPage = safeLazy(() =>
  import('@/features/tenant-admin/scenarios').then((m) => ({
    default: m.TenantScenariosListPage,
  })),
)
const TenantScenarioDetailPage = safeLazy(() =>
  import('@/features/tenant-admin/scenarios').then((m) => ({
    default: m.TenantScenarioDetailPage,
  })),
)
const TenantCreateScenarioPage = safeLazy(() =>
  import('@/features/tenant-admin/scenarios').then((m) => ({
    default: m.TenantCreateScenarioPage,
  })),
)
const TenantRulesListPage = safeLazy(() =>
  import('@/features/tenant-admin/scenarios').then((m) => ({
    default: m.TenantRulesListPage,
  })),
)
const TenantRuleDetailPage = safeLazy(() =>
  import('@/features/tenant-admin/scenarios').then((m) => ({
    default: m.TenantRuleDetailPage,
  })),
)
const TenantCreateRulePage = safeLazy(() =>
  import('@/features/tenant-admin/scenarios').then((m) => ({
    default: m.TenantCreateRulePage,
  })),
)

// ADR 0023 Sprint 7 Wave B — Platform Administration (multi-tenant management).
// All platform/* routes are lazy-loaded and gated on Me.is_platform_admin (ADR-0023).
//
// Phase 3 (tenant-view switcher): PlatformTenantProvider is now promoted to the
// AppShell level (wraps all authenticated routes). PlatformLayout and
// AdminScenariosLayout continue to mount their own providers — these become nested
// no-ops (React context nesting: the inner provider overrides the outer for its
// subtree, but since both start with null-selected state and share the same store
// singleton, they are functionally equivalent to a single provider).
import { PlatformLayout } from '@/features/platform'
import { PlatformTenantProvider } from '@/features/platform/context'
const PlatformTenantListPage = safeLazy(() =>
  import('@/features/platform/TenantListPage').then((m) => ({
    default: m.TenantListPage,
  })),
)
const PlatformCreateTenantPage = safeLazy(() =>
  import('@/features/platform/CreateTenantPage').then((m) => ({
    default: m.CreateTenantPage,
  })),
)
const PlatformTenantProfilePage = safeLazy(() =>
  import('@/features/platform/TenantProfilePage').then((m) => ({
    default: m.TenantProfilePage,
  })),
)
const PlatformBankingSettingsPage = safeLazy(() =>
  import('@/features/platform/settings/BankingSettingsPage').then((m) => ({
    default: m.BankingSettingsPage,
  })),
)
const PlatformFraudSettingsPage = safeLazy(() =>
  import('@/features/platform/settings/FraudSettingsPage').then((m) => ({
    default: m.FraudSettingsPage,
  })),
)
const PlatformAmlSettingsPage = safeLazy(() =>
  import('@/features/platform/settings/AmlSettingsPage').then((m) => ({
    default: m.AmlSettingsPage,
  })),
)
const PlatformCasesSettingsPage = safeLazy(() =>
  import('@/features/platform/settings/CasesSettingsPage').then((m) => ({
    default: m.CasesSettingsPage,
  })),
)
const PlatformNotificationsSettingsPage = safeLazy(() =>
  import('@/features/platform/settings/NotificationsSettingsPage').then((m) => ({
    default: m.NotificationsSettingsPage,
  })),
)
const PlatformReportingSettingsPage = safeLazy(() =>
  import('@/features/platform/settings/ReportingSettingsPage').then((m) => ({
    default: m.ReportingSettingsPage,
  })),
)
const PlatformIngestionPage = safeLazy(() =>
  import('@/features/platform/IngestionPage').then((m) => ({
    default: m.IngestionPage,
  })),
)
const PlatformConnectorsPage = safeLazy(() =>
  import('@/features/platform/ConnectorsPage').then((m) => ({
    default: m.ConnectorsPage,
  })),
)
const PlatformMappingsPage = safeLazy(() =>
  import('@/features/platform/MappingsPage').then((m) => ({
    default: m.MappingsPage,
  })),
)
const PlatformTenantApprovalsPage = safeLazy(() =>
  import('@/features/platform/TenantApprovalsPage').then((m) => ({
    default: m.TenantApprovalsPage,
  })),
)
const PlatformTenantUsersPage = safeLazy(() =>
  import('@/features/platform/TenantUsersPage').then((m) => ({
    default: m.TenantUsersPage,
  })),
)
const PlatformTenantAuditPage = safeLazy(() =>
  import('@/features/platform/TenantAuditPage').then((m) => ({
    default: m.TenantAuditPage,
  })),
)
// Sprint 7 checklist adapter: resolves publicId → tenant.code → redirects to
// existing /admin/databus/tenants/:code/checklist (ActivationChecklistPage).
const PlatformChecklistPage = safeLazy(() =>
  import('@/features/platform/PlatformChecklistPage').then((m) => ({
    default: m.PlatformChecklistPage,
  })),
)
// ADR 0023 P4-web — Tenant domain management (DNS + SSL + activation).
const PlatformTenantDomainsPage = safeLazy(() =>
  import('@/features/platform/domains/TenantDomainsPage').then((m) => ({
    default: m.TenantDomainsPage,
  })),
)
// ADR-0025 — Platform tenant entitlement management (permission ceiling).
const PlatformTenantEntitlementsPage = safeLazy(() =>
  import('@/features/platform/entitlements/TenantEntitlementsPage').then((m) => ({
    default: m.TenantEntitlementsPage,
  })),
)
// Maker-checker policy — per-tenant dual-approval configuration surface.
const PlatformMakerCheckerPolicyPage = safeLazy(() =>
  import('@/features/platform/settings/MakerCheckerPolicyPage').then((m) => ({
    default: m.MakerCheckerPolicyPage,
  })),
)

/** Route-level auth gate. Redirects to /login (with ?next=…) if /me 401s. */
function RequireAuth() {
  const { data, isLoading, isError } = useMe()
  const location = useLocation()
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={24} label="Loading session" />
      </div>
    )
  }
  if (isError || !data) {
    // Preserve the original deep link so the login form can route the
    // analyst back after a successful sign-in.
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }
  return <Outlet />
}

/**
 * Index-route landing (bookmark, logo click, manual URL, refresh — every
 * entry OTHER than the login-form submit path, which has its own identical
 * call in features/auth/routes.tsx).
 *
 * Previously hard-coded `<Navigate to="/inbox" replace />` for every
 * authenticated principal. /inbox is scope:'tenant' (navigation.ts SCOPE
 * MODEL, ADR-0024) — a platform admin with no tenant selected (PLATFORM
 * mode) has no tenant context, and the backend's cross-tenant
 * `AllTenants=true` fan-out for platform scope (authorised and deliberate;
 * internal/api/alerts_handler.go) would render as an unfiltered
 * multi-tenant alert queue inside a UI built for one tenant — a
 * wrong-surface bug, not a data leak, but still the wrong landing.
 *
 * Delegates to the same `pickLandingRoute` ladder the login form uses
 * (landing.ts) so the two entry points can never diverge again.
 *
 * `isPlatformMode` mirrors MyDashboard.tsx's predicate exactly:
 * `isPlatformAdmin && tenantViewId === null`. Unlike the login-form call
 * site (where tenantViewId is provably always null — selectedTenantId is
 * session-only useState seeded fresh per page load), THIS entry point can
 * be hit after a platform admin has already switched into a tenant view
 * via the tenant-view switcher, so we read the live context instead of
 * assuming null.
 */
function IndexLanding() {
  const { data: me } = useMe()
  const isPlatformAdmin = useIsPlatformAdmin()
  const tenantCtx = usePlatformTenantOptional()
  const selectedTenantId = tenantCtx?.selectedTenantId ?? null
  const isPlatformMode = isPlatformAdmin && selectedTenantId === null
  return <Navigate to={pickLandingRoute(me, { isPlatformMode })} replace />
}

/** Permission-scoped route guard. */
function RequirePermissionRoute({ any }: { any: readonly string[] }) {
  const allowed = useCanAny(...any)
  if (!allowed) {
    return <AccessDenied variant="permission" />
  }
  return <Outlet />
}

/**
 * Platform-admin-only route guard.
 *
 * Reads `Me.is_platform_admin` via useIsPlatformAdmin() WITHOUT the
 * `system:admin` short-circuit. Required for surfaces that the backend
 * (ADR-0023/ADR-0024) restricts to platform-scope actors.
 *
 * A tenant-admin holding `system:admin` for their own tenant but with
 * `is_platform_admin: false` will hit this guard and see the
 * AccessDenied card instead of triggering backend 403s. This makes the
 * authorization boundary explicit and operator-visible.
 *
 * Renders <AccessDenied variant="platform"> (the shared 403 surface)
 * so the message and layout are consistent with RequirePermissionRoute.
 * Does NOT redirect — the explicit 403 card makes the boundary visible.
 */
function RequirePlatformAdminRoute() {
  const isPlatformAdmin = useIsPlatformAdmin()
  if (!isPlatformAdmin) {
    return <AccessDenied variant="platform" />
  }
  return <Outlet />
}

/**
 * Tenant-self route guard.
 *
 * Blocks platform administrators who are operating in PLATFORM MODE (no tenant
 * selected in the tenant-view switcher) from reaching the /tenant-admin/* surfaces.
 *
 * These surfaces call /tenants/self/* endpoints — the "self" tenant is derived
 * from the JWT. A platform-admin JWT has no tenant in scope, so every request
 * returns 404 or 403 at the backend. The guard surfaces a clear AccessDenied card
 * BEFORE the page renders and fires those failing requests.
 *
 * Allowed through:
 *   (a) Tenant-scoped users (is_platform_admin: false) — their JWT carries the
 *       tenant; inner RequirePermissionRoute still gates on the specific perm.
 *   (b) Platform admins in TENANT-VIEW mode (is_platform_admin: true AND the
 *       tenant-view switcher has a tenant selected) — the HTTP client attaches the
 *       target-tenant-id header so the backend can derive the correct tenant.
 *
 * Blocked:
 *   Platform admins in PLATFORM mode (is_platform_admin: true AND no tenant
 *   selected in the switcher) — direct URL / bookmark navigation.
 *
 * Implementation note: usePlatformTenantOptional() returns null when no provider
 * is in scope, which should never happen because PlatformTenantProvider wraps all
 * authenticated routes at AppShell level (Phase 3). null is treated as "no tenant
 * selected" (fail-closed), so any missing-provider edge case also blocks.
 *
 * Exported for unit testing (RequireTenantScopedRoute.test.tsx). Do not import
 * this component from application code outside of the router.
 */
export function RequireTenantScopedRoute() {
  const isPlatformAdmin = useIsPlatformAdmin()
  const tenantCtx = usePlatformTenantOptional()
  const selectedTenantId = tenantCtx?.selectedTenantId ?? null
  // Block only: platform admin in platform mode (no tenant selected).
  if (isPlatformAdmin && !selectedTenantId) {
    return <AccessDenied variant="tenant" />
  }
  return <Outlet />
}

function RouteErrorBoundary() {
  const error = useRouteError() as Error
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-xl font-semibold">Page crashed</h1>
      <p className="max-w-md text-sm text-muted-fg">{error?.message ?? 'Unknown error'}</p>
      <Button onClick={() => window.location.reload()}>Reload</Button>
    </div>
  )
}

function RouteSpinner() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner size={20} label="Loading page" />
    </div>
  )
}

// PUBLIC — web-based installer (ADR 0020). Lazy-loaded because the
// page is only ever seen on first boot of a host; analysts never
// touch it. The installer daemon at 127.0.0.1:9099 gates whether the
// page renders the wizard or a "disabled" message.
const InstallerPage = safeLazy(() =>
  import('@/features/installer').then((m) => ({ default: m.InstallerPage })),
)

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/login/forgot',
    element: <ForgotPasswordPage />,
  },
  // PUBLIC — installer wizard. Outside RequireAuth because on a fresh
  // host the operator has no session yet. ADR 0020.
  {
    path: '/install',
    element: (
      <Suspense fallback={<RouteSpinner />}>
        <InstallerPage />
      </Suspense>
    ),
  },
  // PUBLIC — developer onboarding portal. Explicitly outside RequireAuth
  // so visiting /developers/onboarding does not trigger the /me check.
  {
    path: 'developers/onboarding',
    element: <OnboardingLayout />,
    children: [
      { index: true, element: <OnboardingHome /> },
      { path: ':slug', element: <OnboardingDoc /> },
      { path: 'examples/:slug', element: <OnboardingExample /> },
    ],
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        // Phase 3 (tenant-view switcher): PlatformTenantProvider is promoted to
        // wrap ALL authenticated routes inside AppShell. For tenant-scoped users
        // (is_platform_admin: false) this is a no-op — the context starts null and
        // TenantSelectorBand gates on useIsPlatformAdmin(). For platform admins it
        // gives every operational route access to the selected tenant context so the
        // HTTP client can attach the target-tenant-id header automatically.
        element: (
          <PlatformTenantProvider>
            <Outlet />
          </PlatformTenantProvider>
        ),
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <IndexLanding /> },

          // AF-309 — unified inbox; the canonical landing for any
          // role with inbox:read. Aggregates alerts + pending blocks
          // + SAR reviews + handovers + notifications under one
          // 3-pane workspace.
          {
            element: <RequirePermissionRoute any={['inbox:read']} />,
            children: [
              {
                path: 'inbox',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <InboxPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            // Backend canonical gate: cases:read.
            // There is no alerts:read permission in the backend; alerts_handler.go
            // gates on cases:read. The old alerts:read was a web-invented permission
            // that incorrectly blocked analysts (who have cases:read) from this surface.
            element: <RequirePermissionRoute any={['cases:read']} />,
            children: [
              { path: 'alerts', element: <AlertsQueuePage /> },
              { path: 'alerts/:id', element: <AlertDetailPage /> },
            ],
          },
          {
            element: <RequirePermissionRoute any={['cases:read']} />,
            children: [
              { path: 'cases', element: <CasesListPage /> },
              { path: 'cases/:id', element: <CaseDetailPage /> },
              // AF-404 — Network Explorer. Requires customer reads
              // for the seed search; the underlying network endpoint
              // already gates on customers:read, so here we follow
              // suit. Cases:read holders are the analyst audience.
              {
                path: 'investigations/network',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <NetworkExplorerPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            // /admin/challenges — operational challenge feed (analyst + compliance view).
            // Backend canonical permission: audit:read (challenges_admin_handler.go).
            // analyst, compliance_officer, and viewer all hold audit:read.
            // The config surface (/admin/challenges/settings) stays in the
            // system:admin group above.
            element: <RequirePermissionRoute any={['audit:read']} />,
            children: [
              {
                path: 'admin/challenges',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ChallengesPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            element: <RequirePermissionRoute any={['customers:read']} />,
            children: [
              {
                path: 'customers',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <CustomersListPage />
                  </Suspense>
                ),
              },
              {
                path: 'customers/:id',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <Customer360Page />
                  </Suspense>
                ),
              },
              { path: 'customers/:id/accounts/:nuban', element: <AccountDetailPage /> },
            ],
          },
          {
            element: <RequirePermissionRoute any={['transactions:read']} />,
            children: [
              { path: 'transactions', element: <TransactionsListPage /> },
              { path: 'transactions/:id', element: <TransactionDetailPage /> },
            ],
          },
          {
            element: <RequirePermissionRoute any={['blocks:read']} />,
            children: [
              { path: 'blocks', element: <ActiveBlocksPage /> },
              {
                element: <RequirePermissionRoute any={['blocks:request']} />,
                children: [{ path: 'blocks/new', element: <BlockRequestPage /> }],
              },
              {
                element: <RequirePermissionRoute any={['blocks:approve']} />,
                children: [
                  { path: 'blocks/approvals', element: <BlockApprovalQueuePage /> },
                ],
              },
              { path: 'blocks/:id', element: <BlockDetailPage /> },
            ],
          },
          {
            // AF-SS-066 + AF-SS-072 — screening tenant surfaces.
            // Dashboard, History, and detail views are tenant-scoped: the backend
            // enforces row-level tenancy server-side, so screening:read is the
            // correct gate for all three. The Console is platform-admin-only (below).
            element: <RequirePermissionRoute any={['screening:read']} />,
            children: [
              {
                path: 'screening',
                element: <Navigate to="/screening/dashboard" replace />,
              },
              {
                path: 'screening/dashboard',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ScreeningDashboardPage />
                  </Suspense>
                ),
              },
              {
                // AF-SS-072: /screening/sources was folded into /screening/console.
                // Redirect to the canonical console URL. Non-platform users who follow
                // this redirect will hit the RequirePlatformAdminRoute gate on
                // /screening/console and see the access-denied card — that is the
                // correct UX (the console is a platform-level surface). Sending them
                // to /screening/dashboard instead was overly defensive and broke the
                // smoke test assertion that /screening/sources renders the Screening
                // Console heading (the smoke user is is_platform_admin:true).
                path: 'screening/sources',
                element: <Navigate to="/screening/console" replace />,
              },
              {
                path: 'screening/history',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ScreeningHistoryPage />
                  </Suspense>
                ),
              },
              {
                path: 'screening/screenings/:publicId',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ScreeningDetailPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            // Adverse-media surface — gated by adverse_media:read.
            // Sister track to /screening/* per the spec at
            // documentation/adverse-media/adverse-media-spec.md.
            element: <RequirePermissionRoute any={['adverse_media:read']} />,
            children: [
              {
                path: 'adverse-media',
                element: <Navigate to="/adverse-media/dashboard" replace />,
              },
              {
                path: 'adverse-media/dashboard',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: 'adverse-media/feeds',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaFeedsPage />
                  </Suspense>
                ),
              },
              {
                path: 'adverse-media/classifications',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaClassificationsPage />
                  </Suspense>
                ),
              },
              {
                path: 'adverse-media/classifications/:publicId',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaClassificationDetailPage />
                  </Suspense>
                ),
              },
              {
                path: 'adverse-media/related-parties/:publicId',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaRelatedPartyDetailPage />
                  </Suspense>
                ),
              },
              {
                path: 'adverse-media/merchants/:publicId',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaMerchantDetailPage />
                  </Suspense>
                ),
              },
              {
                path: 'adverse-media/profiles',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaProfilesPage />
                  </Suspense>
                ),
              },
              {
                path: 'adverse-media/eval-set',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AdverseMediaEvalSetPage />
                  </Suspense>
                ),
              },
              {
                // LLM-prompt admin — gated on adverse_media:prompts_admin.
                // Analyst has adverse_media:read but NOT prompts_admin.
                // Inner guard required so URL-navigation is also blocked.
                path: 'adverse-media/prompts',
                element: <RequirePermissionRoute any={['adverse_media:prompts_admin']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <AdverseMediaPromptsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            // admin/jobs — Scheduled Jobs: platform-scoped (system:admin,
            // tenant_admin lacks it). RequirePlatformAdminRoute prevents
            // tenant-admins from accessing even with system:admin held for
            // their tenant (ADR-0024 scope enforcement).
            //
            // M4 fix (Phase 4): The nav entry for "Scheduled Jobs" also requires
            // system:admin (navigation.ts + sidebar-sections.ts). Adding an inner
            // RequirePermissionRoute any=['system:admin'] makes the route guard
            // consistent with the nav gate. In practice every platform admin holds
            // system:admin (the production setup), but a narrow-permission platform
            // service account lacking it will get an explicit 403 rather than
            // landing on an empty/broken page. Consistent with the other Operations
            // routes (Services Control, Ingestion Engine, Batch, etc.).
            element: <RequirePlatformAdminRoute />,
            children: [
              {
                element: <RequirePermissionRoute any={['system:admin']} />,
                children: [
                  {
                    path: 'admin/jobs',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <JobsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            element: <RequirePermissionRoute any={['sars:read']} />,
            children: [
              {
                path: 'sars',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <SarListPage />
                  </Suspense>
                ),
              },
              {
                path: 'sars/new',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <SarWizardPage />
                  </Suspense>
                ),
              },
              {
                path: 'sars/:id',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <SarWizardPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            element: <RequirePermissionRoute any={['reports:read']} />,
            children: [
              {
                path: 'reports',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ReportsCatalogPage />
                  </Suspense>
                ),
              },
              // AF-137 — schedule before :id so the dynamic segment doesn't
              // shadow it.
              {
                path: 'reports/schedule',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ReportsSchedulePage />
                  </Suspense>
                ),
              },
              {
                path: 'reports/:id',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ReportDetailPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            element: <RequirePermissionRoute any={['ml:read']} />,
            children: [
              {
                path: 'ml',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MlModelsPage />
                  </Suspense>
                ),
              },
              // AF-133 — compare must come before /ml/:id so it isn't
              // consumed by the dynamic-segment route.
              {
                path: 'ml/compare',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MlModelComparePage />
                  </Suspense>
                ),
              },
              {
                path: 'ml/monitoring',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MlMonitoringPage />
                  </Suspense>
                ),
              },
              {
                path: 'ml/governance',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MlGovernancePage />
                  </Suspense>
                ),
              },
              {
                path: 'ml/:id',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MlModelDetailPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            element: <RequirePermissionRoute any={['handovers:read']} />,
            children: [
              { path: 'handovers', element: <HandoverLogPage /> },
              // AF-135 — end-shift before :id so it isn't shadowed.
              { path: 'handovers/end-shift', element: <EndShiftPage /> },
              { path: 'handovers/:id', element: <HandoverAcknowledgePage /> },
            ],
          },
          { path: 'notifications', element: <NotificationsCenterPage /> },
          { path: 'notifications/preferences', element: <NotificationPreferencesPage /> },
          // /access-requests — Access Requests inbox (approver view).
          // access:grant and system:admin are both platform-scoped permissions
          // (tenant_admin lacks them per ADR-0024). RequirePlatformAdminRoute
          // gates the inbox so tenant-admins who hold system:admin for their
          // tenant cannot reach this surface.
          //
          // /access-requests/mine — every authenticated user may view their
          // own submitted requests. This surface is intentionally open to all
          // signed-in users; no platform gate needed.
          {
            element: <RequirePlatformAdminRoute />,
            children: [
              {
                path: 'access-requests',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <AccessRequestsInboxPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'access-requests/mine',
            element: (
              <Suspense fallback={<RouteSpinner />}>
                <MyAccessRequestsPage />
              </Suspense>
            ),
          },
          {
            element: <RequirePermissionRoute any={['workflow:read']} />,
            children: [
              {
                path: 'workflow',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <QueueDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: 'workflow/state-machine',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <StateMachineViewPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            // Platform-scoped routes — require Me.is_platform_admin (ADR-0024).
            // These surfaces are all classified scope:'platform' in navigation.ts:
            // Scenarios, Rules, API Keys, Webhooks, Bank Configuration,
            // Challenge Plane Settings, Maker-Checker, Communications, Fraud→AML.
            //
            // Previously gated on RequirePermissionRoute any={['system:admin']},
            // which let tenant-admins (system:admin, is_platform_admin:false)
            // through. RequirePlatformAdminRoute enforces the backend boundary
            // client-side without bypassing it (backend re-checks server-side).
            element: <RequirePlatformAdminRoute />,
            children: [
              // Scenarios subtree — wrapped in AdminScenariosLayout which
              // provides PlatformTenantProvider + TenantSelectorBand.
              // The backend requires ?tenant_public_id for platform actors;
              // the layout makes the tenant selector visible and the context
              // accessible to list/detail/create pages.
              {
                element: <AdminScenariosLayout />,
                children: [
                  { path: 'admin/scenarios', element: <AdminScenariosPage /> },
                  {
                    path: 'admin/scenarios/new',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <CreateScenarioPage />
                      </Suspense>
                    ),
                  },
                  { path: 'admin/scenarios/:code', element: <AdminScenarioDetailPage /> },
                ],
              },
              // Rules subtree — wrapped in AdminRulesLayout which provides
              // TenantSelectorBand. The backend requires ?tenant_public_id
              // for platform-scope callers (mirrors scenarios behaviour).
              // RequirePlatformAdminRoute gate is already enforced by the
              // parent element before this renders.
              {
                element: <AdminRulesLayout />,
                children: [
                  {
                    path: 'admin/rules',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <RulesListPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'admin/rules/new',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <CreateRuleWizardPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'admin/rules/:id',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <EditRulePage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'admin/api-keys',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ApiKeysPage />
                  </Suspense>
                ),
              },
              {
                path: 'admin/webhooks',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <WebhooksPage />
                  </Suspense>
                ),
              },
              // admin/challenges (operational feed) moved to audit:read group above.
              // admin/challenges/settings is the admin-config surface — platform-only.
              {
                path: 'admin/challenges/settings',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ChallengePlaneSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: 'admin/maker-checker',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MakerCheckerPage />
                  </Suspense>
                ),
              },
              {
                path: 'admin/communications',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <CommunicationWorkflowsPage />
                  </Suspense>
                ),
              },
              {
                path: 'admin/communications/deliveries',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <CommunicationDeliveriesPage />
                  </Suspense>
                ),
              },
              {
                path: 'admin/fraud-to-aml',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <FraudToAmlPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            // /admin/ml — Closed-Loop ML Feedback dashboard.
            //
            // AF-ML-CLF: All read paths require ml_feedback:read (analyst tier,
            // ml_feedback role, compliance_officer). Write mutations (settings PATCH,
            // per-model PATCH, Train Now) require ml_feedback:write — the API enforces
            // the distinction server-side and returns 403 for insufficient write perms.
            //
            // This was previously inside the system:admin group, causing a nav/route
            // mismatch: the sidebar showed the link for ml_feedback:read users but the
            // route denied them. Corrected to ml_feedback:read (least privilege, matches
            // the page's own access model documented in MLFeedbackPage.tsx header).
            element: <RequirePermissionRoute any={['ml_feedback:read']} />,
            children: [
              {
                path: 'admin/ml',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MLFeedbackPage />
                  </Suspense>
                ),
              },
            ],
          },
          // ADR-0023: platform-admin-only routes. These were previously inside the
          // system:admin group, but the backend now requires actor_scope_type=platform.
          // RequirePlatformAdminRoute checks Me.is_platform_admin WITHOUT the system:admin
          // short-circuit so tenant-admins (system:admin but is_platform_admin:false) see
          // the access-denied card instead of triggering backend 403s.
          {
            element: <RequirePlatformAdminRoute />,
            children: [
              {
                // Global user + RBAC management — platform admin only.
                // Backend: /admin/users requires actor_scope_type=platform.
                path: 'admin/users',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <UsersAdminPage />
                  </Suspense>
                ),
              },
              {
                // Global role catalogue — platform admin only.
                // Backend: /admin/roles requires actor_scope_type=platform.
                path: 'admin/roles',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <RoleDefinitionsPage />
                  </Suspense>
                ),
              },
              {
                // ADR-0023: Screening Console is a platform-level engine-management
                // surface (source catalogue, cadence control, engine status).
                // Tenant users access tenant-scoped results via /screening/dashboard
                // and /screening/history only (those remain gated on screening:read).
                path: 'screening/console',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ScreeningConsolePage />
                  </Suspense>
                ),
              },
              {
                // AF-DB-FAB-400 — Third-party integrations registry (platform vendor
                // catalogue: Dojah, OFAC, NIBSS, anthropic, etc.). Standing rule:
                // every external system the platform integrates with is managed here.
                // ADR-0023: platform admin only — tenant admins must not configure this.
                path: 'admin/integrations',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <IntegrationsPage />
                  </Suspense>
                ),
              },
              {
                // Platform feature flags — curated toggles (audited + reasoned).
                // Hosts platform.multi_tenant_enabled and future platform-scope gates.
                // ADR-0023: platform admin only — tenant admins must not flip these.
                path: 'admin/platform/feature-flags',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <FeatureFlagsPage />
                  </Suspense>
                ),
              },
              {
                // ADR 0018 — Observability network allowlist.
                // Reclassified scope:'platform' (ADR-0024): propose/approve/emergency
                // are platform-ops-only. tenant_admin has observability:read but
                // cannot drive the allowlist workflow. RequirePlatformAdminRoute is
                // the outer gate; per-section RBAC (read/propose/approve/emergency)
                // is enforced inside AccessControlPage itself.
                path: 'admin/observability/access-control',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <ObservabilityACLPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            // Migration 0102 split this group by audience. Each
            // child specifies its own RequirePermissionRoute so the
            // outer guard isn't doing the wrong thing for any of
            // them. Sidebar + landing.ts mirror these gates.
            //
            // databus:* perms join the outer allow-list (sprint 30)
            // because the Enterprise Databus pages live under this
            // routing group but have their own permission family —
            // a databus operator who doesn't hold any audit/system
            // perm would otherwise be locked out at the outer
            // guard before the inner databus:* check ran.
            element: <RequirePermissionRoute any={['audit:read', 'audit_log:read', 'access_log:read', 'system:read', 'demo:admin', 'system:admin', 'databus:read', 'databus:admin', 'outbox:request', 'outbox:approve']} />,
            children: [
              {
                path: 'admin/audit',
                // Audit log is the per-entity audit trail — admin
                // and compliance_officer (regulatory). Senior
                // analysts read case context via the case timeline
                // instead.
                element: <RequirePermissionRoute any={['audit_log:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <AuditLogPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'admin/ingestion',
                // Ops telemetry — platform-scoped (system:read; tenant_admin lacks it).
                // Inner RequirePlatformAdminRoute enforces the ADR-0024 boundary.
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['system:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <IngestionEnginePage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-016 — Enterprise Databus mapping editor.
                // Platform-scoped (ADR-0024): RequirePlatformAdminRoute added
                // to match scope:'platform' classification in navigation.ts.
                path: 'admin/databus/mappings',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <MappingEditorPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-075 — Reconciliation runs.
                // Platform-scoped (ADR-0024): RequirePlatformAdminRoute added.
                path: 'admin/databus/recon',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <ReconRunsPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-SIM-062 — Banking simulator: runs list + run detail.
                // Sandbox/staging-only invariant is enforced backend-side
                // (the control plane's safety middleware); we still gate
                // on databus:admin so non-admins can't trigger runs.
                // Platform-scoped (ADR-0024): RequirePlatformAdminRoute wraps
                // all simulator routes — scope:'platform' in navigation.ts.
                path: 'admin/databus-simulator',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorRunsListPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'runs/:id',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorRunDetailPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'scenarios',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorScenariosListPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'scenarios/new',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorScenarioEditorPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'scenarios/:slug',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorScenarioEditorPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'profiles/new',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorProfileEditorPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'profiles/:slug',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorProfileEditorPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'chaos',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorChaosRehearsalPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'allowlist',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorAllowlistPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'system',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SimulatorSystemPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // ADR 0023 Sprint 5b — Entry Point B: "Add Connector to Tenant".
                // POST /databus/ingestion-channels saga: atomic connector
                // instance (draft) + adapter (paused) + mapping (draft) +
                // vault refs. Replaces the old 3-POST OnboardingWizard.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/ingestion-channels/new',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <ConfigureIngestionChannelPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // ADR 0023 Sprint 5b — /admin/databus/onboard redirects to
                // the Tenant Management page (Entry Point A).
                // Platform-scoped (ADR-0024).
                path: 'admin/databus/onboard',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:admin']} />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="/admin/databus/tenants" replace />,
                      },
                    ],
                  },
                ],
              },
              {
                // ADR 0023 Sprint 6c — Databus approval queue.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/approvals',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <ApprovalsQueuePage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // ADR 0023 Sprint 6c — Activation checklist per tenant.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/tenants/:code/checklist',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <ActivationChecklistPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-101 — Tenant management routes.
                // Nav and route both gate on databus:admin.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/tenants',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:admin']} />,
                    children: [
                      {
                        // Bare /admin/databus/tenants has no dedicated list
                        // view today — redirect to adapters page.
                        index: true,
                        element: <Navigate to="/admin/databus/adapters" replace />,
                      },
                      {
                        path: ':code',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <TenantDetailPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-101 — Tenant create route: requires databus:admin.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/tenants/new',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <CreateTenantPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-101 — Tenants + adapters management.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/adapters',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <AdaptersPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'new',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <CreateAdapterPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <AdapterDetailPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-103 — DLQ console + entry detail.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/dlq',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <DLQConsolePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: ':id',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <DLQEntryDetailPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-104 — Lineage explorer + per-event detail.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/lineage',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <LineageExplorerPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: ':eventId',
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <LineageDetailPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-106 — Ops dashboard.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <OpsDashboardPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-FAB-300 — Platform settings panel (runtime
                // knobs the React admin edits without a redeploy).
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/settings',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <PlatformSettingsPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-DB-105 — Canonical schema registry browser.
                // Platform-scoped (ADR-0024): scope:'platform' in navigation.ts.
                path: 'admin/databus/schemas',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['databus:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SchemaRegistryPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'admin/system-status',
                // Ops health — platform-scoped (system:read; tenant_admin lacks it).
                // Platform guard prevents tenant-admins from accessing (ADR-0024).
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['system:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <SystemStatusPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-OPS-001 — Services control plane. Wraps
                // scripts/services.sh; system:admin only; backend
                // refuses on APP_ENV=production.
                // Platform-scoped (ADR-0024); tenant-admins cannot reach this.
                path: 'admin/services',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['system:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <ServicesControlPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // AF-403 — unified Batch Processing console. Reads
                // from /admin/batch/runs which gates on system:read.
                // Platform-scoped (ADR-0024); tenant-admins cannot reach this.
                path: 'admin/batch',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['system:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <BatchConsolePage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              // ADR-0026 Phase B: /admin/branches is now platform-only.
              // Tenant admins self-manage via /tenant-admin/branches instead.
              // AdminScenariosLayout provides TenantSelectorBand so the platform admin
              // can inspect a specific tenant's branches while in tenant-view mode.
              {
                path: 'admin/branches',
                element: <RequirePermissionRoute any={['branches:read']} />,
                children: [
                  {
                    element: <AdminScenariosLayout />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <BranchesAdminPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              // ADR-0026 Phase B: /admin/teams is now platform-only.
              // Tenant admins self-manage via /tenant-admin/teams instead.
              // AdminScenariosLayout provides TenantSelectorBand for tenant-view mode.
              {
                path: 'admin/teams',
                element: <RequirePermissionRoute any={['teams:read']} />,
                children: [
                  {
                    element: <AdminScenariosLayout />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <TeamsAdminPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'admin/access-log',
                // Auth events (login / logout / lockout) — security-sensitive.
                // access_log:read is platform-scoped (tenant_admin lacks it).
                // Platform guard ensures tenant-admins are blocked (ADR-0024).
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['access_log:read']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <AccessLogPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'admin/demo-data',
                // demo:admin and system:admin are platform-scoped (ADR-0024).
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['demo:admin', 'system:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <DemoDataConsolePage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // Executive Dashboard — platform-scoped (system:admin only,
                // tenant-admins should not see it per nav classification).
                path: 'dashboard',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: <RequirePermissionRoute any={['system:admin']} />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <ExecutiveDashboardPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                // Risk Heatmap — nav permission: audit_log:read.
                path: 'highlights',
                element: <RequirePermissionRoute any={['audit_log:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <EnterpriseHighlightsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                // BR-040 — HQ rollup dashboard. Nav permission: audit:read.
                path: 'dashboard/hq',
                element: <RequirePermissionRoute any={['audit:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <HQRollupDashboardPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                // Operations Health dashboard. Nav permission: audit:read.
                path: 'operations',
                element: <RequirePermissionRoute any={['audit:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <OperationalDashboardPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                // All Dashboards portfolio. Nav permission: audit:read.
                path: 'portfolio',
                element: <RequirePermissionRoute any={['audit:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <DashboardPortfolioPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                // Role Workspaces. Nav permission: audit:read.
                path: 'role-dashboards',
                element: <RequirePermissionRoute any={['audit:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <RoleBasedDashboardsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                // /my-dashboard — every signed-in user has a home.
                // The page itself adapts to the role (executive →
                // exec dashboard; compliance → compliance dashboard;
                // analyst → personal queue counters; viewer → links
                // to whatever they can actually open).
                path: 'my-dashboard',
                element: (
                  <Suspense fallback={<RouteSpinner />}>
                    <MyDashboardPage />
                  </Suspense>
                ),
              },
              {
                // SCR-008 — Compliance Dashboard. SAR/STR pipeline +
                // CBN regulatory return schedule + compliance calendar.
                // Calls /sars/stats (sars:read) and /reports/schedule (reports:read).
                // audit:read is included so compliance_officers (who may hold audit:read
                // but not yet sars:read) can reach the page in narrow-permission tenants.
                // The sidebar nav uses audit:read as well — keeping them aligned.
                path: 'compliance',
                element: <RequirePermissionRoute any={['sars:read', 'reports:read', 'audit:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <ComplianceDashboardPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                // AF-406 — Consortium Intelligence feed.
                path: 'intelligence/consortium',
                element: <RequirePermissionRoute any={['consortium:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <ConsortiumPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                // Trend Analytics. Nav permission: audit:read.
                path: 'analytics',
                element: <RequirePermissionRoute any={['audit:read']} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <AnalyticsDashboardPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              // ADR 0013 L6 — outbox replay maker-checker admin surface.
              // outbox:* permissions are platform-scoped (tenant_admin lacks them).
              // RequirePlatformAdminRoute enforces the ADR-0024 boundary;
              // inner RequirePermissionRoute further narrows to replay perms.
              {
                path: 'admin/outbox-replay',
                element: <RequirePlatformAdminRoute />,
                children: [
                  {
                    element: (
                      <RequirePermissionRoute
                        any={['outbox:request', 'outbox:approve']}
                      />
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<RouteSpinner />}>
                            <OutboxReplayPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // ADR-0026 Phase B (fix) — All /tenant-admin/* routes share an outer guard.
          //
          // RequireTenantScopedRoute fires FIRST: it blocks platform admins in
          // platform mode (no tenant selected in the switcher) from reaching these
          // surfaces. These pages call /tenants/self/* — the backend derives the
          // tenant from the JWT. A platform JWT has no tenant in scope, so every
          // request would 404 / 403. The guard shows AccessDenied before any fetch.
          //
          // Allowed through:
          //   (a) Tenant-scoped users (is_platform_admin: false) — their JWT carries
          //       the tenant; inner RequirePermissionRoute still gates on the perm.
          //   (b) Platform admins in TENANT-VIEW mode (switcher has a tenant selected)
          //       — HTTP client attaches target-tenant-id header for the backend.
          //
          // Note: The nav already hides these items from platform-mode admins via
          // canSeeNavItem(scope:'tenant') returning false. This route guard is an
          // explicit second gate covering direct URL / bookmark navigation.
          {
            element: <RequireTenantScopedRoute />,
            children: [
              // ADR-0023 — Tenant self-service administration.
              // tenant_users:admin is the canonical permission for tenant-scope user
              // management. system:admin short-circuit applies, so tenant admins
              // holding system:admin can also reach this surface.
              {
                element: <RequirePermissionRoute any={['tenant_users:admin']} />,
                children: [
                  {
                    path: 'tenant-admin/users',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantSelfUsersPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // ADR-0025 — Tenant-scoped role management.
              // tenant_roles:read is required; scope:'tenant' (system:admin short-circuit applies).
              // DO NOT share or link to the platform /admin/roles surface.
              // Role identity is always public_id (UUID) — never bare name.
              {
                element: <RequirePermissionRoute any={['tenant_roles:read']} />,
                children: [
                  {
                    path: 'tenant-admin/roles',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantRolesPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Tenant permissions catalogue — read-only reference view.
              // tenant_permissions:read is required; scope:'tenant'.
              // Shows the permission vocabulary grantable within this tenant's ceiling.
              // DO NOT link or share with the platform /admin/permissions endpoint.
              // Tenant is always derived server-side from JWT — no :tenantId in the URL.
              {
                element: <RequirePermissionRoute any={['tenant_permissions:read']} />,
                children: [
                  {
                    path: 'tenant-admin/permissions',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantPermissionsCataloguePage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // ADR-0025 Follow-on / Slice 3 / Slice 5 / Slice 8 / Slice 9 — Tenant-internal maker-checker approvals inbox.
              // ANY of: tenant_roles:approve | tenant_branches:approve |
              //         tenant_teams:approve | tenant_sso:approve | tenant_api_keys:approve |
              //         tenant_webhooks:approve | tenant_bank_settings:approve |
              //         tenant_challenge_settings:approve
              // is required; scope:'tenant'.
              // Shows submitted/approved/applied/rejected role, branch, team, SSO, API key,
              // webhook, bank settings, and challenge settings change requests.
              // Maker cannot approve/reject/apply their own submissions (server-enforced).
              // Per-row entity-type perm enforced server-side + UI hint.
              {
                element: <RequirePermissionRoute any={['tenant_roles:approve', 'tenant_branches:approve', 'tenant_teams:approve', 'tenant_sso:approve', 'tenant_api_keys:approve', 'tenant_webhooks:approve', 'tenant_bank_settings:approve', 'tenant_challenge_settings:approve']} />,
                children: [
                  {
                    path: 'tenant-admin/approvals',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantApprovalsInboxPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // ADR-0026 Phase B — Tenant self-service branch management.
              // tenant_branches:read is required; scope:'tenant'.
              // Tenant is ALWAYS derived server-side from JWT — no :tenantId in the URL.
              // DO NOT share or link to the platform /admin/branches surface from here.
              {
                element: <RequirePermissionRoute any={['tenant_branches:read']} />,
                children: [
                  {
                    path: 'tenant-admin/branches',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantBranchesPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // ADR-0026 Phase B — Tenant self-service team management.
              // tenant_teams:read is required; scope:'tenant'.
              // Tenant is ALWAYS derived server-side from JWT — no :tenantId in the URL.
              // Member picker uses /tenants/self/users, NOT the platform-only /admin/users.
              // DO NOT share or link to the platform /admin/teams surface from here.
              {
                element: <RequirePermissionRoute any={['tenant_teams:read']} />,
                children: [
                  {
                    path: 'tenant-admin/teams',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantTeamsPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Tenant session management.
              // tenant_users:admin is required; scope:'tenant'.
              // Tenant is ALWAYS derived server-side from JWT — no :tenantId in the URL.
              // Client must never send a tenant id. Platform admins are redirected by
              // the backend to /admin/sessions with 400 "use_platform_path".
              {
                element: <RequirePermissionRoute any={['tenant_users:admin']} />,
                children: [
                  {
                    path: 'tenant-admin/sessions',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantSessionsPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Tenant self-service SSO configuration.
              // tenant_sso:read is required for the read view; tenant_sso:admin
              // is additionally required for the edit form (checked inside the page).
              // Tenant is ALWAYS derived server-side from JWT — no :tenantId in URL.
              // Mutations are maker-checker gated by default (security-sensitive).
              {
                element: <RequirePermissionRoute any={['tenant_sso:read']} />,
                children: [
                  {
                    path: 'tenant-admin/sso',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantSSOConfigPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Tenant self-service JIT access approval queue.
              // Gated on tenant_jit:approve (scope:'tenant').
              // tenant_jit:read is NeverGrantableToTenantRole — read access is
              // implied by the approve permission (backend catalogue intent).
              // Self-approval is blocked server-side (403 cannot_self_approve).
              // DO NOT link or share with the platform /admin/jit-access surface.
              {
                element: <RequirePermissionRoute any={['tenant_jit:approve']} />,
                children: [
                  {
                    path: 'tenant-admin/jit-access',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantJITAccessPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Slice 5 — Tenant self-service API key management.
              // Gated on tenant_api_keys:read (scope:'tenant').
              // Write affordances (create, revoke) additionally checked inside the page.
              // Revoke mutations are maker-checker gated by default (api_key.revoke).
              // Uses /tenants/self/api-keys — tenant derived server-side from JWT.
              // DO NOT link or share with platform /admin/api-keys.
              {
                element: <RequirePermissionRoute any={['tenant_api_keys:read']} />,
                children: [
                  {
                    path: 'tenant-admin/api-keys',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantAPIKeysPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Slice 6 — Tenant self-service webhook management.
              // Gated on tenant_webhooks:read (scope:'tenant').
              // Write affordances (create, enable, disable) additionally checked inside page.
              // Creation is maker-checker gated (webhook.create; entity_type:
              //   tenant_webhook_creation). Enable and disable are single-operator.
              // Uses /tenants/self/webhooks — tenant derived server-side from JWT.
              // DO NOT link or share with platform /admin/webhooks.
              {
                element: <RequirePermissionRoute any={['tenant_webhooks:read']} />,
                children: [
                  {
                    path: 'tenant-admin/webhooks',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantWebhooksPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Slice 7 — Tenant self-service access-request approver inbox.
              // Gated on tenant_access:approve (scope:'tenant').
              // tenant_access:read is NeverGrantableToTenantRole — read access is
              // implied by the approve permission (backend catalogue intent).
              // Uses /tenants/self/access-requests — tenant derived server-side from JWT.
              // DISTINCT from /tenant-admin/jit-access (role elevation; different surface).
              // DISTINCT from /access-requests (platform global inbox; uses access:grant).
              {
                element: <RequirePermissionRoute any={['tenant_access:approve']} />,
                children: [
                  {
                    path: 'tenant-admin/access-requests',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantAccessRequestsPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Slice 8 — Tenant self-service banking settings.
              // Gated on tenant_bank_settings:read (scope:'tenant').
              // Write affordances (propose) additionally require tenant_bank_settings:admin.
              // All proposals go through maker-checker (entity_type: tenant_banking_settings_update).
              // Uses /tenants/self/bank-settings — tenant derived server-side from JWT.
              {
                element: <RequirePermissionRoute any={['tenant_bank_settings:read']} />,
                children: [
                  {
                    path: 'tenant-admin/bank-settings',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantBankSettingsPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // Slice 9 — Tenant self-service challenge plane settings.
              // Gated on tenant_challenge_settings:read (scope:'tenant').
              // Write affordances (propose) additionally require tenant_challenge_settings:admin.
              // All proposals go through maker-checker (entity_type: tenant_challenge_settings_update).
              // LIVE: liveness_min_score, liveness_face_match_min_score.
              // DEFERRED: vendor pins + deadline/attempts (inline-liveness channel not yet shipped).
              // Uses /tenants/self/challenge-settings — tenant derived server-side from JWT.
              // DO NOT link or share with platform /admin/challenges/settings (system:admin only).
              {
                element: <RequirePermissionRoute any={['tenant_challenge_settings:read']} />,
                children: [
                  {
                    path: 'tenant-admin/challenge-settings',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantChallengeSettingsPage />
                      </Suspense>
                    ),
                  },
                ],
              },

              // ADR-0023 Track 4 — Tenant self-service scenario management.
              // Gated on scenarios:read (tenant-scope). Backend returns 404 for
              // cross-tenant or platform-actor access; the scope:'tenant' nav
              // gate keeps platform-admins away from the tenant-self surfaces.
              {
                element: <RequirePermissionRoute any={['scenarios:read']} />,
                children: [
                  {
                    path: 'tenant-admin/scenarios',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantScenariosListPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenant-admin/scenarios/new',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantCreateScenarioPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenant-admin/scenarios/:code',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantScenarioDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenant-admin/rules',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantRulesListPage />
                      </Suspense>
                    ),
                  },
                  // NOTE: 'new' MUST be registered before ':id' so the literal
                  // path segment "new" is not parsed as a rule ID.
                  {
                    path: 'tenant-admin/rules/new',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantCreateRulePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenant-admin/rules/:id',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <TenantRuleDetailPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },

          // ADR 0023 Sprint 7 Wave B — Platform Administration.
          // /platform/* routes are gated on Me.is_platform_admin (ADR-0023).
          // PlatformLayout provides PlatformTenantProvider + TenantSelectorBand + Outlet.
          {
            path: 'platform',
            element: <RequirePlatformAdminRoute />,
            children: [
              {
                element: <PlatformLayout />,
                children: [
                  // Index: tenant list
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformTenantListPage />
                      </Suspense>
                    ),
                  },
                  // Create new tenant
                  {
                    path: 'tenants/new',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformCreateTenantPage />
                      </Suspense>
                    ),
                  },
                  // Tenant profile (tabs: profile / domain / login / lifecycle)
                  {
                    path: 'tenants/:publicId',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformTenantProfilePage />
                      </Suspense>
                    ),
                  },
                  // Settings sub-routes (per-domain versioned settings)
                  {
                    path: 'tenants/:publicId/banking',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformBankingSettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenants/:publicId/fraud',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformFraudSettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenants/:publicId/aml',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformAmlSettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenants/:publicId/case-workflow',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformCasesSettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenants/:publicId/notification',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformNotificationsSettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'tenants/:publicId/reporting-retention',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformReportingSettingsPage />
                      </Suspense>
                    ),
                  },
                  // ADR 0023 P4-web — Domain management (DNS verification + SSL/TLS)
                  {
                    path: 'tenants/:publicId/domains',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformTenantDomainsPage />
                      </Suspense>
                    ),
                  },
                  // Ingestion channels
                  {
                    path: 'tenants/:publicId/ingestion',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformIngestionPage />
                      </Suspense>
                    ),
                  },
                  // Connector instances
                  {
                    path: 'tenants/:publicId/connectors',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformConnectorsPage />
                      </Suspense>
                    ),
                  },
                  // Field mappings
                  {
                    path: 'tenants/:publicId/mappings',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformMappingsPage />
                      </Suspense>
                    ),
                  },
                  // Maker-checker approvals (inbox)
                  {
                    path: 'tenants/:publicId/maker-checker',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformTenantApprovalsPage />
                      </Suspense>
                    ),
                  },
                  // Maker-checker policy configuration (dual-approval settings)
                  {
                    path: 'tenants/:publicId/maker-checker-policy',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformMakerCheckerPolicyPage />
                      </Suspense>
                    ),
                  },
                  // Tenant user management (S13 — UI-complete, backend-PENDING)
                  {
                    path: 'tenants/:publicId/users',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformTenantUsersPage />
                      </Suspense>
                    ),
                  },
                  // ADR-0025 — Platform tenant entitlement management.
                  // Gate: RequirePlatformAdminRoute (ancestor). Tenant admins
                  // MUST NOT see or reach this page — the route is behind the
                  // platform-admin RequirePlatformAdminRoute outer guard AND the
                  // sub-nav link is inside TenantProfilePage (platform layout only).
                  {
                    path: 'tenants/:publicId/entitlements',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformTenantEntitlementsPage />
                      </Suspense>
                    ),
                  },
                  // Activation checklist adapter (resolves publicId → code → redirect)
                  {
                    path: 'tenants/:publicId/checklist',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformChecklistPage />
                      </Suspense>
                    ),
                  },
                  // Per-tenant audit log
                  {
                    path: 'tenants/:publicId/audit',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <PlatformTenantAuditPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            // Settings Hub — platform-scoped config surface (AMBIGUOUS: system:admin
            // only, defaulting to scope:'platform' per navigation.ts classification).
            // RequirePlatformAdminRoute prevents tenant-admins from accessing it
            // (ADR-0024). Inner permission check retained for defense-in-depth.
            element: <RequirePlatformAdminRoute />,
            children: [
              {
                element: <RequirePermissionRoute any={['system:admin']} />,
                children: [
                  {
                    path: 'settings',
                    element: (
                      <Suspense fallback={<RouteSpinner />}>
                        <SettingsHubPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          { path: '*', element: <Navigate to="/alerts" replace /> },
        ],
      },
        ],
      },
    ],
  },
])
